import {
  isBundleEntitlementKey,
  isRewardCodeId,
  PERMANENT_PURCHASE_DEFINITIONS,
} from "@/features/payment/paymentDefinitions";
import type { HoverIdlePaymentState } from "@/features/payment/types";
import { isSupportedLocale } from "@/i18n/runtime";
import { isNotation } from "@/shared/format/numberFormat";
import { isHelpTopicId } from "@/features/help/helpTopics";
import {
  compareHuge,
  isHugeNumber,
  isNonNegativeHuge,
  type HugeNumber,
} from "@/shared/numbers";
import { isHovSkillHoldMs } from "@/features/hoverIdle/hov/hovSkillDefinitions";
import { isWallStartChargeMs } from "@/features/hoverIdle/wall/wallStartCharge";
import {
  NHOV_BRANCH_IDS,
  normalizeNHovBranchExperienceByBranch,
  normalizeNHovEffectBoundaryCaps,
} from "../nHov/nHovBranches";
import { isNHovThemeHue } from "../nHov/nHovCandidateTheme";
import {
  HOV_AMPLIFIER_ENTRY_DEFINITIONS,
  type HovAmplifierEntryState,
  type HovAmplifierState,
} from "../hov/hovAmplifierModel";
import { MPM_ASSIGNMENT_IDS } from "../mpm/mpmDefinitions";
import {
  isHovGainUpgradeCellIndex,
  toHovGainUpgradeMpTargetKey,
} from "../mpm/mpmHovGainUpgradeTargets";
import type { MpmState } from "../mpm/mpmState";
import { isNHovBranchId } from "../nHov/nHovBranches";
import { HOV_AMPLIFIER_ENTRY_IDS } from "../hov/hovAmplifierModel";

const HOV_AMPLIFIER_ENTRY_ID_SET = new Set<string>(HOV_AMPLIFIER_ENTRY_IDS);
import type {
  HoverIdleConfig,
  HoverIdleCumulativeStatsState,
  HoverIdleEconomyState,
  HoverIdleHovCellState,
  HoverIdleGridTier,
  HoverIdleHovTargetGridState,
  HoverIdleResetAutomationState,
  HoverIdleSettingsState,
  HovTimBalanceSnapshot,
  MHovState,
  NHovState,
  OHovState,
} from "../types";
import {
  isAmplifierUpgradeGuardMs,
  isHoverChargeDuration,
  isHoverToggleDuration,
  isMHovChargeDurationMs,
  isNHovAnimationSpeed,
  isNHovChargeDurationMs,
  isOHovChargeDurationMs,
} from "./hoverIdleSettingsValues";
import { HOV_CELL_COUNT, TIM_CELL_COUNT } from "./hoverIdleState";
import {
  EXPLORATION_BIOME_IDS,
  type ExplorationState,
} from "../exploration/explorationTypes";
import {
  DEFAULT_EXPLORATION_DEFINITION,
  EXPLORATION_MP_SLOT_SEQUENCE,
  isExplorationMpSlotId,
} from "../exploration/explorationDefinitions";

export function assertHoverIdleConfig(config: HoverIdleConfig): void {
  assertFinitePositive(config.hovGainPerSecond, "hovGainPerSecond");
  assertFinitePositive(config.timGainPerSecond, "timGainPerSecond");
  assertFinitePositive(config.upgradeCost, "upgradeCost");
  assertFinitePositive(config.upgradeGainIncrease, "upgradeGainIncrease");
  assertFinitePositive(config.upgradeCostMultiplier, "upgradeCostMultiplier");
  assertFinitePositive(config.timUpgradeCost, "timUpgradeCost");
  assertFinitePositive(config.timUpgradeGainIncrease, "timUpgradeGainIncrease");
  assertFinitePositive(
    config.timUpgradeCostMultiplier,
    "timUpgradeCostMultiplier",
  );
  if (config.upgradeCostMultiplier <= 1) {
    throw new Error("upgradeCostMultiplier must be greater than 1");
  }
  if (config.timUpgradeCostMultiplier <= 1) {
    throw new Error("timUpgradeCostMultiplier must be greater than 1");
  }
}

export function assertHoverIdleEconomyState(
  state: HoverIdleEconomyState,
): void {
  assertHugeNonNegative(state.hov, "economy.hov");
  assertHugeNonNegative(state.tim, "economy.tim");
  assertFiniteNonNegative(state.fai, "economy.fai");
  assertHugeNonNegative(state.hovGainPerSecond, "economy.hovGainPerSecond");
  assertHugePositive(state.timGainPerSecond, "economy.timGainPerSecond");
  assertHiddenGrowthState(state.hiddenGrowth);
  assertHugePositive(state.upgradeCost, "economy.upgradeCost");
  assertHugePositive(state.timUpgradeCost, "economy.timUpgradeCost");
  assertNonNegativeInteger(
    state.purchasedUpgrades.hovGain,
    "economy.purchasedUpgrades.hovGain",
  );
  assertNonNegativeInteger(
    state.purchasedUpgrades.timGain,
    "economy.purchasedUpgrades.timGain",
  );
  if (typeof state.unlocks.tabs.hovTarget !== "boolean") {
    throw new Error("economy.unlocks.tabs.hovTarget must be a boolean");
  }
  if (typeof state.unlocks.tabs.hovTimBalance !== "boolean") {
    throw new Error("economy.unlocks.tabs.hovTimBalance must be a boolean");
  }
  if (typeof state.unlocks.tabs.hovTimGainBalance !== "boolean") {
    throw new Error("economy.unlocks.tabs.hovTimGainBalance must be a boolean");
  }
  if (typeof state.unlocks.tabs.hovSkill !== "boolean") {
    throw new Error("economy.unlocks.tabs.hovSkill must be a boolean");
  }
  if (typeof state.unlocks.effects.hov.gridIncrease1 !== "boolean") {
    throw new Error(
      "economy.unlocks.effects.hov.gridIncrease1 must be a boolean",
    );
  }
  assertNonNegativeInteger(state.prestige.count, "economy.prestige.count");
  assertFiniteNonNegative(state.prestige.points, "economy.prestige.points");
  assertHovTargetGridState(state.targetGrid, "economy.targetGrid");
  assertHovGainUnlockCosts(
    state.minHovGainUnlockCostsByTargetIndex,
    "economy.minHovGainUnlockCostsByTargetIndex",
  );
  assertHovGainMilestoneTriggerCosts(
    state.minHovGainMilestoneTriggerCostsByMilestoneIndex,
    "economy.minHovGainMilestoneTriggerCostsByMilestoneIndex",
  );
  assertTimTargetGridState(state.timTargetGrid, "economy.timTargetGrid");
  assertHovTimBalanceState(state.hovTimBalance);
  if (typeof state.isTimHovActivationEnabled !== "boolean") {
    throw new Error("economy.isTimHovActivationEnabled must be a boolean");
  }
  if (state.mHov !== undefined) {
    assertMHovState(state.mHov);
  }
  if (state.nHov !== undefined) {
    assertNHovState(state.nHov);
  }
  if (state.oHov !== undefined) {
    assertOHovState(state.oHov);
  }
  if (state.resetAutomation !== undefined) {
    assertResetAutomationState(state.resetAutomation);
  }
  assertFiniteNonNegative(state.lastAnyResetAtMs, "economy.lastAnyResetAtMs");
  if (state.mpm !== undefined) {
    assertMpmState(state.mpm, "economy.mpm");
  }
  if (state.exploration !== undefined) {
    assertExplorationState(state.exploration, "economy.exploration");
  }
  if (state.hovAmplifier !== undefined) {
    assertHovAmplifierState(state.hovAmplifier);
  }
}

function assertResetAutomationState(
  state: HoverIdleResetAutomationState,
): void {
  if (
    typeof state.minimumGameplayIntervalCarryMs !== "object" ||
    state.minimumGameplayIntervalCarryMs === null
  ) {
    throw new Error(
      "economy.resetAutomation.minimumGameplayIntervalCarryMs must be an object",
    );
  }
  assertFiniteNonNegative(
    state.minimumGameplayIntervalCarryMs.mHov,
    "economy.resetAutomation.minimumGameplayIntervalCarryMs.mHov",
  );
  assertFiniteNonNegative(
    state.minimumGameplayIntervalCarryMs.nHov,
    "economy.resetAutomation.minimumGameplayIntervalCarryMs.nHov",
  );
  assertFiniteNonNegative(
    state.minimumGameplayIntervalCarryMs.oHov,
    "economy.resetAutomation.minimumGameplayIntervalCarryMs.oHov",
  );
}

export function assertHovAmplifierState(state: HovAmplifierState): void {
  assertHugeNonNegative(state.ap, "economy.hovAmplifier.ap");
  assertFiniteNonNegative(state.apExponent, "economy.hovAmplifier.apExponent");
  if (!Array.isArray(state.entries)) {
    throw new Error("economy.hovAmplifier.entries must be an array");
  }
  if (state.entries.length !== HOV_AMPLIFIER_ENTRY_DEFINITIONS.length) {
    throw new Error(
      `economy.hovAmplifier.entries must contain exactly ${HOV_AMPLIFIER_ENTRY_DEFINITIONS.length} entries`,
    );
  }
  HOV_AMPLIFIER_ENTRY_DEFINITIONS.forEach((definition, index) => {
    const entry = state.entries[index] as HovAmplifierEntryState | undefined;
    if (entry === undefined || entry.id !== definition.id) {
      throw new Error(
        `economy.hovAmplifier.entries[${index}].id must be "${definition.id}"`,
      );
    }
    const path = `economy.hovAmplifier.entries[${index}]`;
    if (
      typeof entry.gaugeValue !== "number" ||
      !Number.isFinite(entry.gaugeValue) ||
      entry.gaugeValue < 0 ||
      entry.gaugeValue > 1
    ) {
      throw new Error(`${path}.gaugeValue must be a number in [0, 1]`);
    }
    assertNonNegativeInteger(
      entry.enhancementLevel,
      `${path}.enhancementLevel`,
    );
    if (entry.enhancementLevel < definition.initialEnhancementLevel) {
      throw new Error(
        `${path}.enhancementLevel must be at least ${definition.initialEnhancementLevel}`,
      );
    }
    const boost = state.cumulativeBoosts[definition.id];
    assertHugeNonNegative(
      boost,
      `economy.hovAmplifier.cumulativeBoosts.${definition.id}`,
    );
  });
}

export function assertHovGainUnlockCosts(
  costs: readonly (HugeNumber | null)[],
  path: string,
): void {
  if (!Array.isArray(costs)) {
    throw new Error(`${path} must be an array`);
  }
  if (costs.length !== HOV_CELL_COUNT) {
    throw new Error(`${path} must contain exactly ${HOV_CELL_COUNT} costs`);
  }
  for (let i = 0; i < costs.length; i += 1) {
    const cost = costs[i];
    if (cost === null) continue;
    if (!isHugeNumber(cost) || !isNonNegativeHuge(cost)) {
      throw new Error(
        `${path}[${i}] must be a non-negative HugeNumber or null`,
      );
    }
  }
}

// HG milestone trigger costs have one entry per reached milestone, so the list
// is variable length unlike the fixed per-cell unlock cost record.
export function assertHovGainMilestoneTriggerCosts(
  costs: readonly (HugeNumber | null)[],
  path: string,
): void {
  if (!Array.isArray(costs)) {
    throw new Error(`${path} must be an array`);
  }
  for (let i = 0; i < costs.length; i += 1) {
    const cost = costs[i];
    if (cost === null) continue;
    if (!isHugeNumber(cost) || !isNonNegativeHuge(cost)) {
      throw new Error(
        `${path}[${i}] must be a non-negative HugeNumber or null`,
      );
    }
  }
}

export function assertMHovState(state: MHovState): void {
  assertNonNegativeInteger(state.count, "economy.mHov.count");
  assertHugePositive(state.hgMultiplier, "economy.mHov.hgMultiplier");
  assertHugeNonNegative(
    state.currentLoopHovEarned,
    "economy.mHov.currentLoopHovEarned",
  );
  assertHugeNonNegative(
    state.maxHovSinceLastMHov,
    "economy.mHov.maxHovSinceLastMHov",
  );
  assertHugeNonNegative(
    state.lastRewardBaseHov,
    "economy.mHov.lastRewardBaseHov",
  );
}

export function assertNHovState(state: NHovState): void {
  assertNonNegativeInteger(state.count, "economy.nHov.count");
  if (
    typeof state.branchExperienceByBranch !== "object" ||
    state.branchExperienceByBranch === null ||
    Array.isArray(state.branchExperienceByBranch)
  ) {
    throw new Error("economy.nHov.branchExperienceByBranch must be an object");
  }
  const branchExperienceByBranch = normalizeNHovBranchExperienceByBranch(
    state.branchExperienceByBranch,
  );
  for (const branchId of NHOV_BRANCH_IDS) {
    assertHugeNonNegative(
      branchExperienceByBranch[branchId],
      `economy.nHov.branchExperienceByBranch.${branchId}`,
    );
  }
  const effectBoundaryCaps = normalizeNHovEffectBoundaryCaps(
    state.effectBoundaryCaps,
  );
  if (typeof effectBoundaryCaps.hgDiscountCostDivisor !== "boolean") {
    throw new Error(
      "economy.nHov.effectBoundaryCaps.hgDiscountCostDivisor must be a boolean",
    );
  }
  const continuousEffectProgressByBranch =
    state.continuousEffectProgressByBranch;
  if (
    continuousEffectProgressByBranch !== undefined &&
    (typeof continuousEffectProgressByBranch !== "object" ||
      continuousEffectProgressByBranch === null ||
      Array.isArray(continuousEffectProgressByBranch))
  ) {
    throw new Error(
      "economy.nHov.continuousEffectProgressByBranch must be an object",
    );
  }
  if (continuousEffectProgressByBranch !== undefined) {
    for (const branchId of NHOV_BRANCH_IDS) {
      const value = continuousEffectProgressByBranch[branchId];
      if (value !== undefined && value !== true) {
        throw new Error(
          `economy.nHov.continuousEffectProgressByBranch.${branchId} must be true when present`,
        );
      }
    }
  }
}

export function assertOHovState(state: OHovState): void {
  assertNonNegativeInteger(state.count, "economy.oHov.count");
  if (typeof state.isUnlocked !== "boolean") {
    throw new Error("economy.oHov.isUnlocked must be a boolean");
  }
  assertHugeNonNegative(state.power, "economy.oHov.power");
  assertHugeNonNegative(state.hovCore, "economy.oHov.hovCore");
  assertOHovRunRecords(state.records);
}

export function assertOHovRunRecords(records: OHovState["records"]): void {
  if (typeof records !== "object" || records === null) {
    throw new Error("economy.oHov.records must be an object");
  }
  assertFiniteNonNegative(
    records.currentRunStartedAtMs,
    "economy.oHov.records.currentRunStartedAtMs",
  );
  assertNullableFiniteNonNegative(
    records.lastRunDurationMs,
    "economy.oHov.records.lastRunDurationMs",
  );
  assertNullableFiniteNonNegative(
    records.fastestRunDurationMs,
    "economy.oHov.records.fastestRunDurationMs",
  );
  assertFiniteNonNegative(
    records.currentRunGameElapsedMs,
    "economy.oHov.records.currentRunGameElapsedMs",
  );
  assertNullableFiniteNonNegative(
    records.fastestRunGameDurationMs,
    "economy.oHov.records.fastestRunGameDurationMs",
  );
}

function assertNullableFiniteNonNegative(
  value: number | null,
  name: string,
): void {
  if (value === null) return;
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    throw new Error(`${name} must be a finite non-negative number or null`);
  }
}

export function assertHovTimBalanceState(state: HovTimBalanceSnapshot): void {
  assertNonNegativeInteger(state.total, "economy.hovTimBalance.total");
  assertNonNegativeInteger(
    state.unassigned,
    "economy.hovTimBalance.unassigned",
  );
  assertNonNegativeInteger(
    state.hovAllocation,
    "economy.hovTimBalance.hovAllocation",
  );
  assertNonNegativeInteger(
    state.timAllocation,
    "economy.hovTimBalance.timAllocation",
  );
  assertNonNegativeInteger(
    state.hovGainAllocation,
    "economy.hovTimBalance.hovGainAllocation",
  );
  assertNonNegativeInteger(
    state.timGainAllocation,
    "economy.hovTimBalance.timGainAllocation",
  );
  const sum =
    state.unassigned +
    state.hovAllocation +
    state.timAllocation +
    state.hovGainAllocation +
    state.timGainAllocation;
  if (sum !== state.total) {
    throw new Error(
      "economy.hovTimBalance allocations must sum to total (unassigned + hov + tim + hovGain + timGain)",
    );
  }
}

export function assertMpmState(state: MpmState, path: string): void {
  if (
    typeof state.assignments !== "object" ||
    state.assignments === null ||
    Array.isArray(state.assignments)
  ) {
    throw new Error(`${path}.assignments must be an object`);
  }
  for (const assignmentId of MPM_ASSIGNMENT_IDS) {
    assertNonNegativeInteger(
      state.assignments[assignmentId],
      `${path}.assignments.${assignmentId}`,
    );
  }
  assertMpmAssignmentSection(
    state.hovTargets,
    `${path}.hovTargets`,
    assertHovTargetAssignment,
  );
  assertMpmAssignmentSection(
    state.hovGainUpgrades,
    `${path}.hovGainUpgrades`,
    assertHovGainUpgradeAssignment,
  );
  assertMpmMHovExecutionState(state.mhovExecution, `${path}.mhovExecution`);
  assertMpmNHovExecutionState(state.nhovExecution, `${path}.nhovExecution`);
  assertMpmOHovExecutionState(state.ohovExecution, `${path}.ohovExecution`);
  assertMpmAssignmentSection(
    state.amplifierPanels,
    `${path}.amplifierPanels`,
    assertAmplifierPanelAssignment,
  );
  assertMpmAssignmentSection(
    state.amplifierUpgrades,
    `${path}.amplifierUpgrades`,
    assertAmplifierUpgradeAssignment,
  );
  if (typeof state.exploration.enabled !== "boolean") {
    throw new Error(`${path}.exploration.enabled must be a boolean`);
  }
  if (!Array.isArray(state.exploration.assignedSlotIds)) {
    throw new Error(`${path}.exploration.assignedSlotIds must be an array`);
  }
  const assignedSlotIds = new Set<string>();
  for (const slotId of state.exploration.assignedSlotIds) {
    if (!isExplorationMpSlotId(slotId)) {
      throw new Error(
        `${path}.exploration.assignedSlotIds contains an unknown slot id`,
      );
    }
    if (assignedSlotIds.has(slotId)) {
      throw new Error(
        `${path}.exploration.assignedSlotIds must not contain duplicates`,
      );
    }
    assignedSlotIds.add(slotId);
  }
}

export function assertExplorationState(
  state: ExplorationState,
  path: string,
): void {
  if (!Number.isSafeInteger(state.biomeLevel) || state.biomeLevel < 1) {
    throw new Error(`${path}.biomeLevel must be a positive safe integer`);
  }
  if (
    !Number.isSafeInteger(state.purchasedMpSlotCount) ||
    state.purchasedMpSlotCount < 0 ||
    state.purchasedMpSlotCount > EXPLORATION_MP_SLOT_SEQUENCE.length
  ) {
    throw new Error(
      `${path}.purchasedMpSlotCount must identify a valid sequence prefix`,
    );
  }
  assertFiniteNonNegative(
    state.cumulativeTimAddedMs,
    `${path}.cumulativeTimAddedMs`,
  );
  if (typeof state.timAccelerationEnabled !== "boolean") {
    throw new Error(`${path}.timAccelerationEnabled must be a boolean`);
  }
  for (const biomeId of EXPLORATION_BIOME_IDS) {
    const area = state.areas[biomeId];
    assertFiniteNonNegative(
      area.progressMs,
      `${path}.areas.${biomeId}.progressMs`,
    );
    const periodMs = DEFAULT_EXPLORATION_DEFINITION.areas[biomeId].periodMs;
    if (area.progressMs >= periodMs) {
      throw new Error(
        `${path}.areas.${biomeId}.progressMs must be less than ${periodMs}`,
      );
    }
  }
}

function assertMpmMHovExecutionState(
  section: MpmState["mhovExecution"],
  path: string,
): void {
  if (typeof section !== "object" || section === null) {
    throw new Error(`${path} must be an object`);
  }
  if (typeof section.unlocked !== "boolean") {
    throw new Error(`${path}.unlocked must be a boolean`);
  }
  if (typeof section.enabled !== "boolean") {
    throw new Error(`${path}.enabled must be a boolean`);
  }
  if (typeof section.assigned !== "boolean") {
    throw new Error(`${path}.assigned must be a boolean`);
  }
  if (
    !isHugeNumber(section.multiplier) ||
    compareHuge(section.multiplier, 0) <= 0
  ) {
    throw new Error(`${path}.multiplier must be a positive HugeNumber`);
  }
  assertFiniteNonNegative(
    section.minimumResetIntervalMs,
    `${path}.minimumResetIntervalMs`,
  );
}

function assertMpmOHovExecutionState(
  section: MpmState["ohovExecution"],
  path: string,
): void {
  if (typeof section !== "object" || section === null) {
    throw new Error(`${path} must be an object`);
  }
  if (typeof section.unlocked !== "boolean") {
    throw new Error(`${path}.unlocked must be a boolean`);
  }
  if (typeof section.enabled !== "boolean") {
    throw new Error(`${path}.enabled must be a boolean`);
  }
  if (typeof section.assigned !== "boolean") {
    throw new Error(`${path}.assigned must be a boolean`);
  }
  if (
    !isHugeNumber(section.minimumHovCore) ||
    compareHuge(section.minimumHovCore, 0) <= 0
  ) {
    throw new Error(`${path}.minimumHovCore must be a positive HugeNumber`);
  }
  assertFiniteNonNegative(
    section.minimumIntervalMs,
    `${path}.minimumIntervalMs`,
  );
}

function assertMpmNHovExecutionState(
  section: MpmState["nhovExecution"],
  path: string,
): void {
  assertMpmAssignmentSection(section, path, assertNHovExecutionAssignment);
  if (
    typeof section.conditions !== "object" ||
    section.conditions === null ||
    Array.isArray(section.conditions)
  ) {
    throw new Error(`${path}.conditions must be an object`);
  }
  const conditionKeys = Object.keys(section.conditions);
  for (const key of conditionKeys) {
    if (!isNHovBranchId(key)) {
      throw new Error(`${path}.conditions must contain valid NHov branch ids`);
    }
  }
  for (const branchId of NHOV_BRANCH_IDS) {
    const condition = section.conditions[branchId];
    if (typeof condition !== "object" || condition === null) {
      throw new Error(`${path}.conditions.${branchId} must be an object`);
    }
    if (
      !isHugeNumber(condition.minimumEffect) ||
      compareHuge(condition.minimumEffect, 0) <= 0
    ) {
      throw new Error(
        `${path}.conditions.${branchId}.minimumEffect must be a positive HugeNumber`,
      );
    }
    assertFiniteNonNegative(
      condition.minimumTimeSeconds,
      `${path}.conditions.${branchId}.minimumTimeSeconds`,
    );
  }
}

function assertMpmAssignmentSection<TAssignment>(
  section: {
    readonly unlocked: boolean;
    readonly enabled: boolean;
    readonly assignments: readonly TAssignment[];
  },
  path: string,
  assertAssignment: (
    assignment: TAssignment,
    seen: Set<string | number>,
    path: string,
  ) => void,
): void {
  if (typeof section !== "object" || section === null) {
    throw new Error(`${path} must be an object`);
  }
  if (typeof section.unlocked !== "boolean") {
    throw new Error(`${path}.unlocked must be a boolean`);
  }
  if (typeof section.enabled !== "boolean") {
    throw new Error(`${path}.enabled must be a boolean`);
  }
  if (!Array.isArray(section.assignments)) {
    throw new Error(`${path}.assignments must be an array`);
  }
  const seen = new Set<string | number>();
  for (const assignment of section.assignments) {
    assertAssignment(assignment, seen, path);
  }
}

function assertHovTargetAssignment(
  cellIndex: number,
  seen: Set<string | number>,
  path: string,
): void {
  if (
    !Number.isInteger(cellIndex) ||
    cellIndex < 0 ||
    cellIndex >= HOV_CELL_COUNT
  ) {
    throw new Error(
      `${path}.assignments must contain valid Hov target indices`,
    );
  }
  if (seen.has(cellIndex)) {
    throw new Error(`${path}.assignments must not contain duplicates`);
  }
  seen.add(cellIndex);
}

function assertHovGainUpgradeAssignment(
  target: MpmState["hovGainUpgrades"]["assignments"][number],
  seen: Set<string | number>,
  path: string,
): void {
  const key = toHovGainUpgradeMpTargetKey(target);
  if (
    key === null ||
    (target !== "global" &&
      (target.kind !== "cell" || !isHovGainUpgradeCellIndex(target.index)))
  ) {
    throw new Error(
      `${path}.assignments must contain valid Hov Gain Upgrade targets`,
    );
  }
  if (seen.has(key)) {
    throw new Error(`${path}.assignments must not contain duplicates`);
  }
  seen.add(key);
}

function assertNHovExecutionAssignment(
  branchId: MpmState["nhovExecution"]["assignments"][number],
  seen: Set<string | number>,
  path: string,
): void {
  if (!isNHovBranchId(branchId)) {
    throw new Error(`${path}.assignments must contain valid NHov branch ids`);
  }
  if (seen.has(branchId)) {
    throw new Error(`${path}.assignments must not contain duplicates`);
  }
  seen.add(branchId);
}

function assertAmplifierPanelAssignment(
  entryId: MpmState["amplifierPanels"]["assignments"][number],
  seen: Set<string | number>,
  path: string,
): void {
  if (typeof entryId !== "string" || !HOV_AMPLIFIER_ENTRY_ID_SET.has(entryId)) {
    throw new Error(
      `${path}.assignments must contain valid Amplifier Panel entry ids`,
    );
  }
  if (seen.has(entryId)) {
    throw new Error(`${path}.assignments must not contain duplicates`);
  }
  seen.add(entryId);
}

function assertAmplifierUpgradeAssignment(
  entryId: MpmState["amplifierUpgrades"]["assignments"][number],
  seen: Set<string | number>,
  path: string,
): void {
  if (typeof entryId !== "string" || !HOV_AMPLIFIER_ENTRY_ID_SET.has(entryId)) {
    throw new Error(
      `${path}.assignments must contain valid Amplifier upgrade entry ids`,
    );
  }
  if (seen.has(entryId)) {
    throw new Error(`${path}.assignments must not contain duplicates`);
  }
  seen.add(entryId);
}

export function assertHiddenGrowthState(
  state: HoverIdleEconomyState["hiddenGrowth"],
): void {
  if (typeof state !== "object" || state === null || Array.isArray(state)) {
    throw new Error("economy.hiddenGrowth must be an object");
  }
  assertHugeNonNegative(
    state.hovGainBonus,
    "economy.hiddenGrowth.hovGainBonus",
  );
  assertHugeNonNegative(
    state.timGainBonus,
    "economy.hiddenGrowth.timGainBonus",
  );
  assertFiniteNonNegative(
    state.hovTimProgress,
    "economy.hiddenGrowth.hovTimProgress",
  );
}

export function assertHoverIdleSettingsState(
  state: HoverIdleSettingsState,
): void {
  if (typeof state.reduceMotion !== "boolean") {
    throw new Error("settings.reduceMotion must be a boolean");
  }
  if (typeof state.lowPowerMode !== "boolean") {
    throw new Error("settings.lowPowerMode must be a boolean");
  }
  if (!isSupportedLocale(state.language)) {
    throw new Error("settings.language must be a supported locale");
  }
  if (
    state.achievementNotification !== "off" &&
    state.achievementNotification !== "dot" &&
    state.achievementNotification !== "full"
  ) {
    throw new Error("settings.achievementNotification must be off/dot/full");
  }
  if (
    state.wallNotificationMode !== "off" &&
    state.wallNotificationMode !== "dot" &&
    state.wallNotificationMode !== "full"
  ) {
    throw new Error("settings.wallNotificationMode must be off/dot/full");
  }
  if (typeof state.helpNotificationsEnabled !== "boolean") {
    throw new Error("settings.helpNotificationsEnabled must be a boolean");
  }
  if (
    !Array.isArray(state.readHelpTopicIds) ||
    !state.readHelpTopicIds.every(isHelpTopicId)
  ) {
    throw new Error("settings.readHelpTopicIds must be known help topic ids");
  }
  if (!isHovSkillHoldMs(state.hovSkillHoldDurationMs)) {
    throw new Error(
      "settings.hovSkillHoldDurationMs must be a supported hold duration",
    );
  }
  if (!isWallStartChargeMs(state.wallStartChargeDurationMs)) {
    throw new Error(
      "settings.wallStartChargeDurationMs must be a supported wall start charge duration",
    );
  }
  if (!isNotation(state.notation)) {
    throw new Error("settings.notation must be a supported notation");
  }
  if (typeof state.firstAccessTutorialCompleted !== "boolean") {
    throw new Error("settings.firstAccessTutorialCompleted must be a boolean");
  }
  if (
    state.mHovAnimationSpeed !== "normal" &&
    state.mHovAnimationSpeed !== "fast" &&
    state.mHovAnimationSpeed !== "instant"
  ) {
    throw new Error(
      'settings.mHovAnimationSpeed must be "normal", "fast", or "instant"',
    );
  }
  if (!isMHovChargeDurationMs(state.mHovChargeDurationMs)) {
    throw new Error("settings.mHovChargeDurationMs must be 0, 2000, or 5000");
  }
  if (!isNHovAnimationSpeed(state.nHovAnimationSpeed)) {
    throw new Error(
      'settings.nHovAnimationSpeed must be "normal", "fast", or "instant"',
    );
  }
  if (!isNHovChargeDurationMs(state.nHovChargeDurationMs)) {
    throw new Error("settings.nHovChargeDurationMs must be 0, 2000, or 5000");
  }
  if (!isOHovChargeDurationMs(state.oHovChargeDurationMs)) {
    throw new Error("settings.oHovChargeDurationMs must be 0, 2000, or 5000");
  }
  if (typeof state.outsideResetBurstAnimationEnabled !== "boolean") {
    throw new Error(
      "settings.outsideResetBurstAnimationEnabled must be a boolean",
    );
  }
  if (!isHoverToggleDuration(state.hoverToggleDuration)) {
    throw new Error('settings.hoverToggleDuration must be "short" or "normal"');
  }
  if (!isHoverChargeDuration(state.hoverChargeDuration)) {
    throw new Error('settings.hoverChargeDuration must be "short" or "normal"');
  }
  if (!isAmplifierUpgradeGuardMs(state.amplifierUpgradeGuardMs)) {
    throw new Error(
      "settings.amplifierUpgradeGuardMs must be 0, 1000, or 3000",
    );
  }
  if (typeof state.hasSeenAvailableAmplifierUpgradeGuard !== "boolean") {
    throw new Error(
      "settings.hasSeenAvailableAmplifierUpgradeGuard must be a boolean",
    );
  }
  if (typeof state.hasSeenMHovButton !== "boolean") {
    throw new Error("settings.hasSeenMHovButton must be a boolean");
  }
  if (typeof state.hasSeenNHovButton !== "boolean") {
    throw new Error("settings.hasSeenNHovButton must be a boolean");
  }
}

export function assertHoverIdlePaymentState(
  state: HoverIdlePaymentState,
): void {
  assertNonNegativeInteger(state.token, "payment.token");
  if (!Array.isArray(state.redeemedRewardCodes)) {
    throw new Error("payment.redeemedRewardCodes must be an array");
  }
  for (const code of state.redeemedRewardCodes) {
    if (typeof code !== "string") {
      throw new Error("payment.redeemedRewardCodes must contain only strings");
    }
    if (!isRewardCodeId(code)) {
      throw new Error(
        `payment.redeemedRewardCodes contains unknown id: ${code}`,
      );
    }
  }
  if (!Array.isArray(state.entitlements)) {
    throw new Error("payment.entitlements must be an array");
  }
  for (const entitlement of state.entitlements) {
    if (typeof entitlement !== "string") {
      throw new Error("payment.entitlements must contain only strings");
    }
    if (!isBundleEntitlementKey(entitlement)) {
      throw new Error(
        `payment.entitlements contains unknown id: ${entitlement}`,
      );
    }
  }
  if (!Array.isArray(state.claimedEntitlements)) {
    throw new Error("payment.claimedEntitlements must be an array");
  }
  for (const entitlement of state.claimedEntitlements) {
    if (typeof entitlement !== "string") {
      throw new Error("payment.claimedEntitlements must contain only strings");
    }
    if (!isBundleEntitlementKey(entitlement)) {
      throw new Error(
        `payment.claimedEntitlements contains unknown id: ${entitlement}`,
      );
    }
    if (!state.entitlements.includes(entitlement)) {
      throw new Error(
        `payment.claimedEntitlements contains non-owned id: ${entitlement}`,
      );
    }
  }
  if (
    typeof state.purchasedPermanentItems !== "object" ||
    state.purchasedPermanentItems === null
  ) {
    throw new Error("payment.purchasedPermanentItems must be an object");
  }
  for (const definition of PERMANENT_PURCHASE_DEFINITIONS) {
    const value = (
      state.purchasedPermanentItems as unknown as Record<string, unknown>
    )[definition.id];
    if (typeof value !== "number" || !Number.isInteger(value) || value < 0) {
      throw new Error(
        `payment.purchasedPermanentItems.${definition.id} must be a non-negative integer`,
      );
    }
  }
}

export function assertHoverIdleCumulativeStatsState(
  state: HoverIdleCumulativeStatsState,
): void {
  assertHugeNonNegative(state.totalHovEarned, "cumulativeStats.totalHovEarned");
  assertHugeNonNegative(state.totalTimEarned, "cumulativeStats.totalTimEarned");
  assertFiniteNonNegative(
    state.totalFaiEarned,
    "cumulativeStats.totalFaiEarned",
  );
  assertHugeNonNegative(state.totalTimSpent, "cumulativeStats.totalTimSpent");
  assertHugeNonNegative(
    state.lifetimeHovRefinedFromTim,
    "cumulativeStats.lifetimeHovRefinedFromTim",
  );
  assertNonNegativeInteger(
    state.upgradesPurchased,
    "cumulativeStats.upgradesPurchased",
  );
  assertNonNegativeInteger(
    state.hovGainFamilyUpgradesPurchased,
    "cumulativeStats.hovGainFamilyUpgradesPurchased",
  );
  assertNonNegativeInteger(
    state.amplifierUpgradesPurchased,
    "cumulativeStats.amplifierUpgradesPurchased",
  );
  assertNonNegativeInteger(
    state.prestigeCount,
    "cumulativeStats.prestigeCount",
  );
  assertFiniteNonNegative(
    state.onlinePlaytimeSeconds,
    "cumulativeStats.onlinePlaytimeSeconds",
  );
  assertHugeNonNegative(state.maxHov, "cumulativeStats.maxHov");
  if (typeof state.hasExecutedNewHovWithoutMergeHov !== "boolean") {
    throw new Error(
      "cumulativeStats.hasExecutedNewHovWithoutMergeHov must be a boolean",
    );
  }
  if (typeof state.hasExecutedMergeHovWithOnlyOneHovTargetCell !== "boolean") {
    throw new Error(
      "cumulativeStats.hasExecutedMergeHovWithOnlyOneHovTargetCell must be a boolean",
    );
  }
  if (
    typeof state.hasExecutedNHovExceedingNotFullyFreeThreshold !== "boolean"
  ) {
    throw new Error(
      "cumulativeStats.hasExecutedNHovExceedingNotFullyFreeThreshold must be a boolean",
    );
  }
  if (typeof state.hasExecutedMassiveMergeHov !== "boolean") {
    throw new Error(
      "cumulativeStats.hasExecutedMassiveMergeHov must be a boolean",
    );
  }
  if (typeof state.hasExecutedOutsideMhov !== "boolean") {
    throw new Error("cumulativeStats.hasExecutedOutsideMhov must be a boolean");
  }
  if (typeof state.hasExecutedOutsideNhov !== "boolean") {
    throw new Error("cumulativeStats.hasExecutedOutsideNhov must be a boolean");
  }
  if (typeof state.redAmplifierFullChargeReached !== "boolean") {
    throw new Error(
      "cumulativeStats.redAmplifierFullChargeReached must be a boolean",
    );
  }
  assertHugeNonNegative(
    state.totalHovCoreSpent,
    "cumulativeStats.totalHovCoreSpent",
  );
  assertNonNegativeInteger(
    state.mhovExecutedByMpCount,
    "cumulativeStats.mhovExecutedByMpCount",
  );
}

export function assertHovTargetGridState(
  state: HoverIdleHovTargetGridState,
  path: string,
): void {
  assertGridTier(state.gridTier, `${path}.gridTier`);
  assertOptionalNHovThemeHue(state.themeHue, `${path}.themeHue`);
  if (!Array.isArray(state.hovCells)) {
    throw new Error(`${path}.hovCells must be an array`);
  }
  if (state.hovCells.length !== HOV_CELL_COUNT) {
    throw new Error(
      `${path}.hovCells must contain exactly ${HOV_CELL_COUNT} cells`,
    );
  }
  for (let i = 0; i < state.hovCells.length; i += 1) {
    assertHovCellState(state.hovCells[i], `${path}.hovCells[${i}]`, i);
  }
}

function assertOptionalNHovThemeHue(value: unknown, name: string): void {
  if (value === undefined || value === null) return;
  if (!isNHovThemeHue(value)) {
    throw new Error(`${name} must be null or a Hov biome hue`);
  }
}

export function assertTimTargetGridState(
  state: HoverIdleEconomyState["timTargetGrid"],
  path: string,
): void {
  assertGridTier(state.gridTier, `${path}.gridTier`);
  if (!Array.isArray(state.timCells)) {
    throw new Error(`${path}.timCells must be an array`);
  }
  if (state.timCells.length !== TIM_CELL_COUNT) {
    throw new Error(
      `${path}.timCells must contain exactly ${TIM_CELL_COUNT} cells`,
    );
  }
  for (let i = 0; i < state.timCells.length; i += 1) {
    assertTimCellState(state.timCells[i], `${path}.timCells[${i}]`, i);
  }
}

export function assertHovCellState(
  state: HoverIdleHovCellState,
  path: string,
  index: number,
): void {
  if (typeof state.unlocked !== "boolean") {
    throw new Error(`${path}.unlocked must be a boolean`);
  }
  assertHugeNonNegative(state.hovGainMultiplier, `${path}.hovGainMultiplier`);
  if (index === 0 && !state.unlocked) {
    throw new Error(`${path}.unlocked must be true for Hov1`);
  }
  if (state.unlocked && compareHuge(state.hovGainMultiplier, 0) <= 0) {
    throw new Error(`${path}.hovGainMultiplier must be positive when unlocked`);
  }
  if (!state.unlocked && compareHuge(state.hovGainMultiplier, 0) !== 0) {
    throw new Error(`${path}.hovGainMultiplier must be 0 when locked`);
  }
  assertHugePositive(state.upgradeCost, `${path}.upgradeCost`);
  assertNonNegativeInteger(
    state.purchasedUpgrades,
    `${path}.purchasedUpgrades`,
  );
}

export function assertTimCellState(
  state: HoverIdleEconomyState["timTargetGrid"]["timCells"][number],
  path: string,
  index: number,
): void {
  if (typeof state.unlocked !== "boolean") {
    throw new Error(`${path}.unlocked must be a boolean`);
  }
  assertHugeNonNegative(state.timGainMultiplier, `${path}.timGainMultiplier`);
  if (index === 0 && !state.unlocked) {
    throw new Error(`${path}.unlocked must be true for Tim1`);
  }
  if (state.unlocked && compareHuge(state.timGainMultiplier, 0) <= 0) {
    throw new Error(`${path}.timGainMultiplier must be positive when unlocked`);
  }
  if (!state.unlocked && compareHuge(state.timGainMultiplier, 0) !== 0) {
    throw new Error(`${path}.timGainMultiplier must be 0 when locked`);
  }
  assertHugePositive(state.upgradeCost, `${path}.upgradeCost`);
  assertNonNegativeInteger(
    state.purchasedUpgrades,
    `${path}.purchasedUpgrades`,
  );
}

export function assertFinitePositive(value: number, name: string): void {
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error(`${name} must be a finite positive number`);
  }
}

export function assertFiniteNonNegative(value: number, name: string): void {
  if (!Number.isFinite(value) || value < 0) {
    throw new Error(`${name} must be a finite non-negative number`);
  }
}

export function assertHugeNonNegative(value: HugeNumber, name: string): void {
  if (!isHugeNumber(value) || !isNonNegativeHuge(value)) {
    throw new Error(`${name} must be a non-negative HugeNumber`);
  }
}

export function assertHugePositive(value: HugeNumber, name: string): void {
  if (!isHugeNumber(value) || value.lte(0)) {
    throw new Error(`${name} must be a positive HugeNumber`);
  }
}

export function assertNonNegativeInteger(value: number, name: string): void {
  if (!Number.isInteger(value) || value < 0) {
    throw new Error(`${name} must be a non-negative integer`);
  }
}

export function assertGridTier(
  value: number,
  name: string,
): asserts value is HoverIdleGridTier {
  if (value !== 1 && value !== 4 && value !== 9) {
    throw new Error(`${name} must be one of 1, 4, or 9`);
  }
}
